import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface LivePayout {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  status: string;
  bank_name: string | null;
  account_holder: string | null;
  account_number_last4: string;
  note: string | null;
  admin_note: string | null;
  transfer_reference: string | null;
  processed_at: string | null;
  created_at: string;
  requester_name: string;
  requester_type: string | null;
  reference: string;
  paystack_status: string | null;
  paystack_error: string | null;
  transferred_at: string | null;
}

export interface LivePaymentsResponse {
  payouts: LivePayout[];
  balance: Array<{ currency: string; balance: number }>;
}

/** Admin-only feed of payout requests enriched with live Paystack transfer status. */
export const useLivePayments = () =>
  useQuery({
    queryKey: ["admin", "live-payments"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-live-payments");
      if (error) throw error;
      const payload = data as LivePaymentsResponse & { error?: string };
      if (payload?.error) throw new Error(payload.error);
      return { payouts: payload.payouts ?? [], balance: payload.balance ?? [] } as LivePaymentsResponse;
    },
  });

/** Approves a payout request and sends the real ZAR transfer through Paystack. */
export const useApprovePayout = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payoutId: string) => {
      const { data, error } = await supabase.functions.invoke("paystack-payout", {
        body: { payout_id: payoutId },
      });
      if (error) throw error;
      const payload = data as { ok?: boolean; status?: string; reference?: string; error?: string };
      if (payload?.error) throw new Error(payload.error);
      return payload;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "live-payments"] });
      qc.invalidateQueries({ queryKey: ["payout-requests"] });
    },
  });
};
